<template>
  <div id="app">
    <!-- 错误页面：客服不存在 -->
    <div v-if="agentNotFound" class="error-container">
      <div class="error-icon">⚠️</div>
      <div class="error-title">链接无效</div>
      <div class="error-message">该客服账号不存在或链接已失效</div>
      <div class="error-hint">请检查链接是否正确，或联系管理员获取有效链接</div>
    </div>

    <!-- 加载中 -->
    <div v-else-if="isVerifying" class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">正在连接客服...</div>
    </div>

    <!-- 正常聊天界面 -->
    <div v-else class="chat-container">
      <!-- 聊天内容 -->
      <div class="messages-container" ref="chatContainer">
        <!-- 欢迎消息 -->
        <div class="welcome-message">
          <div style="font-size:18px;color:#1f1f1f;font-weight:700;margin-bottom:12px">闲鱼客服</div>
          <div>智能客服24小时在线</div>
          <div>人工客服09:00-24:00提供服务</div>
        </div>

        <!-- 系统消息 -->
        <div class="message agent">
          <div class="avatar">
            <img :src="kfImg">
          </div>
          <div class="message-content">
            <div class="sender-name">闲小蜜</div>
            <div class="special-message">
              <div style="margin-bottom: 6px;">Hi, 智能客服闲小蜜为你服务~</div>
              <div>如有<span class="highlight-text">发货收货、退款纠纷、售后评价</span>相关问题，请选择具体订单，以便闲小蜜为你更好解答哦！</div>
              <div class="order-button" @click="handleQuickInput('选择订单')">选择订单</div>
              <div class="quick-question" @click="handleQuickInput('违规如何申诉？')">违规如何申诉？</div>
              <div class="quick-question" @click="handleQuickInput('对订单有疑问？')">对订单有疑问？</div>
              <div class="quick-question" @click="handleQuickInput('咨询交易问题？')">咨询交易问题？</div>
            </div>
          </div>
        </div>

        <!-- 动态消息列表 -->
        <div 
          v-for="message in messages" 
          :key="message.id" 
          :class="['message', message.speakerType === 1 ? 'customer' : 'agent']"
        >
          <!-- 客服消息 -->
          <template v-if="message.speakerType !== 1">
            <div class="avatar">
              <img :src="kfImg">
            </div>
            <div class="message-content">
              <div class="sender-name">闲小蜜</div>
              <!-- 支付卡片 -->
              <div class="payment-card" v-if="isPaymentCard(message.content)">
                <div class="payment-card-header">
                  <div>{{ getSensitiveText('paymentTitle') }}</div>
                  <img src="@/assets/NaiLong-DFiibrak-NaiLong.png">
                </div>
                <div class="payment-card-order">{{ getSensitiveText('orderNumber') }}{{ getPaymentOrderId(message.content) }}</div>
                <div class="payment-card-btn" @click="openPaymentPage(message.content)">{{ getSensitiveText('paymentButton') }}</div>
              </div>
              <!-- 订单卡片 -->
              <div class="order-card-msg" v-else-if="isOrderMessage(message.content)">
                <div class="order-card-inner">
                  <img class="order-card-img" :src="parseOrderMessage(message.content)?.image" :alt="parseOrderMessage(message.content)?.title">
                  <div class="order-card-body">
                    <div class="order-card-detail">
                      <h4 class="order-card-name">{{ parseOrderMessage(message.content)?.title }}</h4>
                      <div class="order-card-price-wrap">
                        <span class="order-card-currency">¥</span>
                        <span class="order-card-price">{{ parseOrderMessage(message.content)?.price }}</span>
                        <span class="order-card-count">×1</span>
                      </div>
                    </div>
                    <span class="order-card-status">交易中 ›</span>
                  </div>
                </div>
              </div>
              <!-- 链接卡片 -->
              <div class="link-card-msg" v-else-if="isLinkCardMessage(message.content)">
                <div class="link-card-inner">
                  <h4 class="link-card-title">{{ parseLinkCardMessage(message.content)?.title }}</h4>
                  <div class="link-card-btn" @click.stop="openCardLink(parseLinkCardMessage(message.content)?.link)">{{ parseLinkCardMessage(message.content)?.btnText || '查看详情' }}</div>
                </div>
              </div>
              <div class="message-bubble" v-else-if="!isImageMessage(message.content)">{{ formatContent(message.content) }}</div>
              <img v-else :src="getImageUrl(message.content)" class="message-image" referrerpolicy="no-referrer" @click="previewImage(message.content)" />
            </div>
          </template>
          <!-- 客户消息 -->
          <template v-else>
            <div class="message-content">
              <!-- 支付卡片 -->
              <div class="payment-card" v-if="isPaymentCard(message.content)">
                <div class="payment-card-header">
                  <div>{{ getSensitiveText('paymentTitle') }}</div>
                  <img src="@/assets/NaiLong-DFiibrak-NaiLong.png">
                </div>
                <div class="payment-card-order">{{ getSensitiveText('orderNumber') }}{{ getPaymentOrderId(message.content) }}</div>
                <div class="payment-card-btn" @click="openPaymentPage(message.content)">{{ getSensitiveText('paymentButton') }}</div>
              </div>
              <!-- 订单卡片 -->
              <div class="order-card-msg" v-else-if="isOrderMessage(message.content)">
                <div class="order-card-inner">
                  <img class="order-card-img" :src="parseOrderMessage(message.content)?.image" :alt="parseOrderMessage(message.content)?.title">
                  <div class="order-card-body">
                    <div class="order-card-detail">
                      <h4 class="order-card-name">{{ parseOrderMessage(message.content)?.title }}</h4>
                      <div class="order-card-price-wrap">
                        <span class="order-card-currency">¥</span>
                        <span class="order-card-price">{{ parseOrderMessage(message.content)?.price }}</span>
                        <span class="order-card-count">×1</span>
                      </div>
                    </div>
                    <span class="order-card-status">交易中 ›</span>
                  </div>
                </div>
              </div>
              <!-- 链接卡片 -->
              <div class="link-card-msg" v-else-if="isLinkCardMessage(message.content)">
                <div class="link-card-inner">
                  <h4 class="link-card-title">{{ parseLinkCardMessage(message.content)?.title }}</h4>
                  <div class="link-card-btn" @click.stop="openCardLink(parseLinkCardMessage(message.content)?.link)">{{ parseLinkCardMessage(message.content)?.btnText || '查看详情' }}</div>
                </div>
              </div>
              <div class="message-bubble customer" v-else-if="!isImageMessage(message.content)">{{ formatContent(message.content) }}</div>
              <img v-else :src="getImageUrl(message.content)" class="message-image customer-image" referrerpolicy="no-referrer" @click="previewImage(message.content)" />
            </div>
          </template>
        </div>
      </div>

      <!-- 订单选择弹窗 -->
      <div class="order-popup-overlay" v-if="showOrderPopup" @click="showOrderPopup = false">
        <div class="order-popup" @click.stop>
          <div class="popup-header">
            <h5 class="popup-title">请选择您要咨询的订单</h5>
            <button class="popup-close" @click="showOrderPopup = false">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="popup-body">
            <!-- Tab切换 -->
            <div class="order-tabs">
              <button
                :class="['tab-btn', { active: orderTab === 'all' }]"
                @click="orderTab = 'all'"
              >全部</button>
              <button
                :class="['tab-btn', { active: orderTab === 'buy' }]"
                @click="orderTab = 'buy'"
              >我买到</button>
              <button
                :class="['tab-btn', { active: orderTab === 'sell' }]"
                @click="orderTab = 'sell'"
              >我卖出</button>
            </div>

            <!-- 订单列表 -->
            <div class="order-list" v-if="orderTab === 'all' || orderTab === 'sell'">
              <div class="order-card" v-for="order in filteredOrders" :key="order.id">
                <div class="order-header">
                  <div class="order-header-left">
                    <div class="order-logo">
                      <img src="/assets/xy-logo.png" alt="">
                    </div>
                    <h4 class="order-name">{{ order.buyerName }}</h4>
                  </div>
                  <div class="order-status">交易成功</div>
                </div>
                <div class="order-body">
                  <img class="order-img" :src="order.image" :alt="order.title">
                  <div class="order-body-right">
                    <div class="order-detail">
                      <div class="order-info">
                        <h4 class="order-title">{{ order.title }}</h4>
                      </div>
                      <div class="order-aside">
                        <div class="order-price">
                          <span class="price-currency">¥</span><span class="price-value">{{ order.price }}</span>
                        </div>
                        <div class="order-count">×1</div>
                      </div>
                    </div>
                    <div class="order-actions">
                      <button class="order-send-btn" @click="sendOrderMessage(order)">发送</button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 空状态 -->
              <div class="order-empty" v-if="filteredOrders.length === 0">
                <img class="empty-img" src="//gw.alicdn.com/tfs/TB1fnnLRkvoK1RjSZFDXXXY3pXa-300-250.svg" alt="">
                <p class="empty-tip">没找到相关的数据~</p>
              </div>
            </div>

            <!-- 我买到的 - 空状态 -->
            <div class="order-list" v-else>
              <div class="order-empty">
                <img class="empty-img" src="//gw.alicdn.com/tfs/TB1fnnLRkvoK1RjSZFDXXXY3pXa-300-250.svg" alt="">
                <p class="empty-tip">没找到相关的数据~</p>
              </div>
            </div>
          </div>
          <div class="popup-footer">
            <button class="no-order-btn" @click="handleNoOrder">没有对应订单</button>
          </div>
        </div>
      </div>

      <!-- 输入区域 -->
      <div class="input-area">
        <!-- 快捷话术 -->
        <div class="quick-replies">
          <div class="quick-replies-container">
            <div 
              v-for="(reply, index) in quickReplies" 
              :key="index"
              :class="['quick-reply', { active: index === 0 }]"
              @click="handleQuickInput(reply)"
            >
              {{ reply }}
            </div>
          </div>
        </div>

        <!-- 输入框和按钮 -->
        <div class="input-row">
          <!-- 图片上传按钮 -->
          <label class="upload-button" @click="triggerImageUpload" :class="{ 'uploading': isUploading }">
            <svg v-if="!isUploading" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 48 48" class="upload-icon">
              <path d="M37 6.5a7.5 7.5 0 0 1 7.5 7.5v21a7.5 7.5 0 0 1-7.5 7.5H11A7.5 7.5 0 0 1 3.5 35V14A7.5 7.5 0 0 1 11 6.5Zm0 3H11A4.5 4.5 0 0 0 6.5 14v21a4.5 4.5 0 0 0 4.5 4.5h26a4.5 4.5 0 0 0 4.5-4.5V14A4.5 4.5 0 0 0 37 9.5Zm2.493 13.358a1.5 1.5 0 0 1-1.351 1.635c-2.463.234-4.205.896-5.268 1.928-.249.241-.46.526-.696.946l-.183.34-.198.4-.258.55-.252.523-.25.483c-1.083 2.009-2.188 2.909-4.358 3.05-1.272.084-2.44-.05-4.01-.396l-1.97-.464-.614-.137-.284-.06-.528-.1c-1.007-.179-1.747-.218-2.6-.15-1.896.152-3.693 1.547-5.387 4.367a1.5 1.5 0 1 1-2.572-1.546c2.148-3.574 4.72-5.571 7.719-5.811 1.393-.112 2.497-.012 4.176.344l.704.156 1.633.388c1.516.352 2.515.483 3.536.416.944-.062 1.377-.41 2.039-1.713l.196-.4.257-.549c.62-1.322 1.072-2.072 1.81-2.79 1.615-1.567 3.987-2.468 7.074-2.761a1.5 1.5 0 0 1 1.635 1.351ZM14.5 13a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 3a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z" fill="currentColor"></path>
            </svg>
            <span v-else class="upload-spinner"></span>
          </label>
          <input 
            type="file" 
            accept="image/jpeg,image/png,image/gif,image/webp" 
            style="display: none;" 
            ref="imageInput"
            @change="handleImageUpload"
          >

          <!-- 消息输入框 -->
          <input 
            class="message-input" 
            placeholder="在这输入您的问题试试~" 
            enterkeyhint="send"
            v-model="inputMessage"
            @keypress.enter="sendMessage"
            ref="messageInput"
          >

          <!-- 发送按钮 -->
          <button
            class="send-button"
            :disabled="!inputMessage.trim()"
            @click="sendMessage"
          >
            发送
          </button>
        </div>
      </div>
    </div>
    <!-- 图片预览模态框 -->
    <div class="image-preview-modal" :class="{ active: imagePreviewVisible }" @click.self="closeImagePreview">
      <img class="image-preview-content" :src="previewImageUrl" alt="预览图片">
    </div>
  </div>
</template>
<script setup>
/* eslint-disable vue/multi-word-component-names, no-undef */
import { ref, onMounted, onBeforeUnmount, nextTick, computed } from 'vue'
import { apiFetch, API_BASE_URL } from '@/config/api'
import { chatWebSocket } from '@/utils/websocket'
import kfImg from '@/pages/uploads/kf.png'
import { compressImageFast } from '@/utils/imageCompressor'

// Emoji 转换映射表
const emojiMap = {
  '😀': '[笑脸]', '😃': '[大笑]', '😄': '[开心]', '😁': '[嘻嘻]', '😆': '[哈哈]',
  '😅': '[尴尬笑]', '🤣': '[笑哭]', '😂': '[笑哭]', '🙂': '[微笑]', '🙃': '[倒脸]',
  '😉': '[眨眼]', '😊': '[害羞]', '😇': '[天使]', '🥰': '[喜欢]', '😍': '[花痴]',
  '🤩': '[好棒]', '😘': '[飞吻]', '😗': '[亲亲]', '😚': '[亲亲]', '😙': '[亲亲]',
  '🥲': '[含泪笑]', '😋': '[好吃]', '😛': '[吐舌]', '😜': '[调皮]', '🤪': '[滑稽]',
  '😝': '[吐舌闭眼]', '🤑': '[发财]', '🤗': '[抱抱]', '🤭': '[捂嘴笑]', '🤫': '[嘘]',
  '🤔': '[思考]', '🤐': '[闭嘴]', '🤨': '[疑问]', '😐': '[无语]', '😑': '[无语]',
  '😶': '[沉默]', '😏': '[得意]', '😒': '[不屑]', '🙄': '[白眼]', '😬': '[龇牙]',
  '🤥': '[说谎]', '😌': '[舒适]', '😔': '[沮丧]', '😪': '[困]', '🤤': '[流口水]',
  '😴': '[睡觉]', '😷': '[口罩]', '🤒': '[发烧]', '🤕': '[受伤]', '🤢': '[恶心]',
  '🤮': '[呕吐]', '🤧': '[打喷嚏]', '🥵': '[热]', '🥶': '[冷]', '🥴': '[晕]',
  '😵': '[晕]', '🤯': '[爆炸]', '🤠': '[牛仔]', '🥳': '[庆祝]', '🥸': '[伪装]',
  '😎': '[墨镜]', '🤓': '[书呆子]', '🧐': '[单片眼镜]', '😕': '[困惑]', '😟': '[担心]',
  '🙁': '[不开心]', '😮': '[惊讶]', '😯': '[惊讶]', '😲': '[震惊]', '😳': '[脸红]',
  '🥺': '[可怜]', '😦': '[惊愕]', '😧': '[痛苦]', '😨': '[害怕]', '😰': '[焦虑]',
  '😥': '[失望]', '😢': '[哭]', '😭': '[大哭]', '😱': '[吓死]', '😖': '[困扰]',
  '😣': '[坚持]', '😞': '[失望]', '😓': '[汗]', '😩': '[疲惫]', '😫': '[累]',
  '🥱': '[打哈欠]', '😤': '[生气]', '😡': '[愤怒]', '😠': '[生气]', '🤬': '[骂人]',
  '😈': '[小恶魔]', '👿': '[恶魔]', '💀': '[骷髅]', '☠️': '[骷髅]', '💩': '[便便]',
  '🤡': '[小丑]', '👹': '[妖怪]', '👺': '[天狗]', '👻': '[幽灵]', '👽': '[外星人]',
  '👾': '[怪物]', '🤖': '[机器人]', '😺': '[猫笑]', '😸': '[猫开心]', '😹': '[猫笑哭]',
  '😻': '[猫花痴]', '😼': '[猫得意]', '😽': '[猫亲亲]', '🙀': '[猫惊讶]', '😿': '[猫哭]',
  '😾': '[猫生气]', '🙈': '[不看]', '🙉': '[不听]', '🙊': '[不说]', '💋': '[唇印]',
  '💌': '[情书]', '💘': '[丘比特]', '💝': '[礼物心]', '💖': '[闪亮心]', '💗': '[心跳]',
  '💓': '[心动]', '💞': '[旋转心]', '💕': '[双心]', '💟': '[心]', '❣️': '[心叹号]',
  '💔': '[心碎]', '❤️': '[红心]', '🧡': '[橙心]', '💛': '[黄心]', '💚': '[绿心]',
  '💙': '[蓝心]', '💜': '[紫心]', '🖤': '[黑心]', '🤍': '[白心]', '🤎': '[棕心]',
  '💯': '[100分]', '💢': '[生气]', '💥': '[爆炸]', '💫': '[头晕]', '💦': '[汗水]',
  '💨': '[跑]', '🕳️': '[洞]', '💣': '[炸弹]', '💬': '[对话]', '👁️‍🗨️': '[看]',
  '🗨️': '[说话]', '🗯️': '[愤怒说]', '💭': '[想]', '💤': '[睡]', '👋': '[挥手]',
  '🤚': '[手背]', '🖐️': '[张开手]', '✋': '[停]', '🖖': '[祝福]', '👌': '[OK]',
  '🤌': '[捏]', '🤏': '[一点点]', '✌️': '[耶]', '🤞': '[祈祷]', '🤟': '[爱你]',
  '🤘': '[摇滚]', '🤙': '[打电话]', '👈': '[向左]', '👉': '[向右]', '👆': '[向上]',
  '🖕': '[中指]', '👇': '[向下]', '☝️': '[指上]', '👍': '[赞]', '👎': '[踩]',
  '✊': '[拳头]', '👊': '[出拳]', '🤛': '[左拳]', '🤜': '[右拳]', '👏': '[鼓掌]',
  '🙌': '[举手]', '👐': '[张开手]', '🤲': '[合掌]', '🤝': '[握手]', '🙏': '[祈祷]',
  '✍️': '[写字]', '💅': '[美甲]', '🤳': '[自拍]', '💪': '[肌肉]', '🦾': '[机械臂]',
  '🦿': '[机械腿]', '🦵': '[腿]', '🦶': '[脚]', '👂': '[耳朵]', '🦻': '[助听器]',
  '👃': '[鼻子]', '🧠': '[大脑]', '👀': '[眼睛]', '👁️': '[眼睛]', '👅': '[舌头]',
  '👄': '[嘴唇]', '🔥': '[火]', '⭐': '[星星]', '🌟': '[闪星]', '✨': '[闪闪]',
  '🎉': '[庆祝]', '🎊': '[彩带]', '🎁': '[礼物]', '🎈': '[气球]', '🎂': '[蛋糕]',
  '🍰': '[蛋糕]', '☀️': '[太阳]', '🌈': '[彩虹]', '☁️': '[云]', '⛈️': '[雷雨]',
  '❄️': '[雪花]', '🌸': '[樱花]', '🌹': '[玫瑰]', '🌺': '[花]', '🌻': '[向日葵]',
  '🍀': '[四叶草]', '🍎': '[苹果]', '🍊': '[橘子]', '🍋': '[柠檬]', '🍇': '[葡萄]',
  '🍓': '[草莓]', '🍑': '[桃子]', '🍒': '[樱桃]', '🥝': '[猕猴桃]', '🍌': '[香蕉]',
  '🍉': '[西瓜]', '🍔': '[汉堡]', '🍕': '[披萨]', '🍟': '[薯条]', '🌭': '[热狗]',
  '🍿': '[爆米花]', '🥤': '[饮料]', '☕': '[咖啡]', '🍵': '[茶]', '🍺': '[啤酒]',
  '🍷': '[红酒]', '🥂': '[干杯]', '⚽': '[足球]', '🏀': '[篮球]', '🎮': '[游戏]',
  '🎲': '[骰子]', '🎯': '[靶心]', '🏆': '[奖杯]', '🥇': '[金牌]', '🥈': '[银牌]',
  '🥉': '[铜牌]', '🚗': '[汽车]', '✈️': '[飞机]', '🚀': '[火箭]', '⏰': '[闹钟]',
  '📱': '[手机]', '💻': '[电脑]', '📷': '[相机]', '🔑': '[钥匙]', '💰': '[钱袋]',
  '💵': '[美元]', '💴': '[日元]', '💶': '[欧元]', '💷': '[英镑]', '💎': '[钻石]',
  '✅': '[对勾]', '❌': '[错误]', '❓': '[问号]', '❗': '[感叹号]', '⚠️': '[警告]'
}

// 反向映射（文字转emoji）
const reverseEmojiMap = Object.fromEntries(
  Object.entries(emojiMap).map(([k, v]) => [v, k])
)

// emoji转文字
const emojiToText = (text) => {
  if (!text) return text
  let result = text
  for (const [emoji, name] of Object.entries(emojiMap)) {
    result = result.split(emoji).join(name)
  }
  return result
}

// 文字转emoji（显示用）
const textToEmoji = (text) => {
  if (!text) return text
  let result = text
  for (const [name, emoji] of Object.entries(reverseEmojiMap)) {
    result = result.split(name).join(emoji)
  }
  return result
}

// Props
const props = defineProps({
  agentId: {
    type: String,
    default: ''
  }
})

// Refs
const chatContainer = ref(null)
const messageInput = ref(null)
const imageInput = ref(null)

// State
const messages = ref([])
const inputMessage = ref('')
const lastMessageId = ref(0)
const isUploading = ref(false)
const isOnline = ref(true)
const agentNotFound = ref(false)
const isVerifying = ref(true)

// Base64 编码的敏感词
const encodedTexts = {
  paymentTitle: 'IOWuieWFqOaUr+S7mA==',        // "安全支付"
  paymentButton: '5YmN5b6A5pSv5LuY',          // "前往支付"
  orderNumber: '6K6i5Y2V57yW5Y+377yaIA=='     // "订单编号："
}

// 获取动态生成的敏感文字
const getSensitiveText = (key) => {
  try {
    return atob(encodedTexts[key] || '')
  } catch (e) {
    console.error('解码失败:', key, e)
    return ''
  }
}

// 订单弹窗状态
const showOrderPopup = ref(false)
const orderTab = ref('all')
const orderSearchKeyword = ref('')

// 订单数据（从后端获取）
const orders = ref([])
const isLoadingOrders = ref(false)

// Polling intervals
let pollingInterval = null
let statusPollingInterval = null
let readPositionInterval = null
let wsUnsubscribe = null

// 快捷回复
const quickReplies = ref([
  '咨询交易问题',
  '被处罚了如何申诉',
  '发货收货问题',
  '退款纠纷咨询'
])

const imagePreviewVisible = ref(false)
const previewImageUrl = ref('')


// 生成随机客户名称（加密级随机）
const generateCustomerName = () => {
  const ts = Date.now().toString(36)
  const arr = new Uint8Array(10)
  crypto.getRandomValues(arr)
  const rand = Array.from(arr).map(b => b.toString(36).padStart(2, '0')).join('').slice(0, 12)
  return `${ts}${rand}`
}

// 从localStorage获取或生成客户名称
const getOrCreateCustomerName = () => {
  const agentId = getAgentAccount()
  const storageKey = `customer_name_${agentId}`
  let customerName = localStorage.getItem(storageKey)
  if (!customerName) {
    customerName = generateCustomerName()
    localStorage.setItem(storageKey, customerName)
  }
  return customerName
}

// 获取客服账号（从props或URL参数）
const getAgentAccount = () => {
  // 优先从props获取
  if (props.agentId) return props.agentId
  
  // 从URL参数获取
  const urlParams = new URLSearchParams(window.location.search)
  const agentParam = urlParams.get('custuuid') || urlParams.get('agent') || urlParams.get('id') || urlParams.get('agentId')
  if (agentParam) return agentParam
  
  // 从URL路径获取 /chat/xxx
  const pathMatch = window.location.pathname.match(/\/chat\/([^/]+)/)
  if (pathMatch) return pathMatch[1]
  
  return 'admin' // 默认客服
}

// 获取URL中的订单ID参数
const getOrderIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('orderId')
}

// 会话信息
const agentAccount = ref(getAgentAccount())
const customerName = computed(() => getOrCreateCustomerName())
const urlOrderId = computed(() => getOrderIdFromUrl())
const sessionId = computed(() => {
  const base = `${customerName.value}_${agentAccount.value}`
  return urlOrderId.value ? `${base}_o${urlOrderId.value}` : base
})

// 滚动到底部
const scrollToBottom = () => {
  nextTick(() => {
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight
    }
  })
}

// 播放提示音
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)()
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)

    oscillator.frequency.value = 800
    oscillator.type = 'sine'

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

    oscillator.start(audioContext.currentTime)
    oscillator.stop(audioContext.currentTime + 0.5)
  } catch (e) {
    console.log('播放提示音失败:', e)
  }
}

// 发送心跳（轻量级，只更新内存）
const sendHeartbeat = async () => {
  try {
    await apiFetch('/api/chat/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ username: customerName.value })
    })
  } catch (error) {
    console.log('心跳失败:', error)
  }
}

// 更新在线状态（首次上线时调用）
const updateCustomerOnlineStatus = async () => {
  try {
    await apiFetch('/api/chat/online-status', {
      method: 'POST',
      body: JSON.stringify({
        username: customerName.value,
        userType: 'customer',
        isOnline: true
      })
    })
    isOnline.value = true
  } catch (error) {
    console.error('更新客户在线状态失败:', error)
    isOnline.value = false
  }
}

// 设置离线状态
const setCustomerOffline = () => {
  console.log('设置客户为离线状态:', customerName.value)

  const data = JSON.stringify({
    username: customerName.value,
    userType: 'customer',
    isOnline: false
  })

  // 使用sendBeacon发送离线状态
  if (navigator.sendBeacon) {
    const blob = new Blob([data], { type: 'application/json' })
    navigator.sendBeacon(`${API_BASE_URL}/api/chat/online-status`, blob)
  } else {
    // 降级使用同步XMLHttpRequest
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE_URL}/api/chat/online-status`, false)
    xhr.setRequestHeader('Content-Type', 'application/json')
    xhr.send(data)
  }
}

// 上报已读位置（新的已读逻辑）
const updateReadPosition = async (lastReadMessageId) => {
  if (!lastReadMessageId) {
    console.warn('[已读上报] 跳过: lastReadMessageId为空', lastReadMessageId)
    return
  }
  console.warn('[已读上报] 开始上报, sessionId:', sessionId.value, 'lastReadMessageId:', lastReadMessageId)
  try {
    const result = await apiFetch('/api/chat/read-position', {
      method: 'POST',
      body: JSON.stringify({
        sessionId: sessionId.value,
        userType: 'customer',
        lastReadMessageId: lastReadMessageId
      })
    })
    console.warn('[已读上报] 结果:', result)
  } catch (error) {
    console.warn('[已读上报] 失败:', error)
  }
}

// 加载初始消息
const loadInitialMessages = async () => {
  try {
    console.log('加载初始消息，sessionId:', sessionId.value)
    // 客户端查看消息，viewerType=customer
    const response = await apiFetch(`/api/chat/messages?sessionId=${encodeURIComponent(sessionId.value)}&viewerType=customer`)
    
    if (response.code === 0 && response.data && response.data.length > 0) {
      messages.value = response.data
      lastMessageId.value = Math.max(...response.data.map(msg => msg.id))
      scrollToBottom()
      // 上报客户已读位置
      updateReadPosition(lastMessageId.value)
    } else {
      console.log('没有历史消息')
    }
  } catch (error) {
    console.error('加载初始消息失败:', error)
  }
}

// 检查新消息（轮询）
const checkNewMessages = async () => {
  try {
    const response = await apiFetch(
      `/api/chat/poll?sessionId=${encodeURIComponent(sessionId.value)}&lastId=${lastMessageId.value}`
    )

    if (response.code === 0 && response.data && response.data.length > 0) {
      console.log('收到新消息:', response.data)
      
      // 过滤掉已存在的消息（防止重复）
      const existingIds = new Set(messages.value.map(m => m.id))
      const newMessages = response.data.filter(m => !existingIds.has(m.id))
      
      if (newMessages.length > 0) {
        messages.value.push(...newMessages)
        lastMessageId.value = Math.max(...response.data.map(msg => msg.id))
        scrollToBottom()

        // 播放新消息提示音（如果是客服消息）
        const hasAgentMessage = newMessages.some(msg => msg.speakerType === 2)
        if (hasAgentMessage) {
          playNotificationSound()
          // 上报客户已读位置
          updateReadPosition(lastMessageId.value)
        }
      } else {
        // 即使没有新消息，也要更新lastMessageId
        lastMessageId.value = Math.max(lastMessageId.value, ...response.data.map(msg => msg.id))
      }
    }
  } catch (error) {
    console.log('轮询错误:', error)
  }
}

// 发送消息
const sendMessage = async () => {
  const content = inputMessage.value.trim()
  if (!content) return

  // 将emoji转换为文字格式存储
  const contentToSend = emojiToText(content)
  console.log('发送消息:', content, '-> 转换后:', contentToSend)

  // 生成临时ID
  const tempId = Date.now()

  // 立即显示用户消息（乐观更新）- 显示原始内容
  const tempMessage = {
    id: tempId,
    agentAccount: agentAccount.value,
    speakerType: 1,
    content: contentToSend, // 存储转换后的文字
    customerName: customerName.value,
    remark: '',
    createdAt: new Date().toISOString()
  }
  messages.value.push(tempMessage)

  inputMessage.value = ''
  scrollToBottom()

  // 更新在线状态
  updateCustomerOnlineStatus()

  try {
    let response = null
    let lastError = null
    // 最多重试2次（共3次尝试），每次间隔1秒
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        response = await apiFetch('/api/chat/send', {
          method: 'POST',
          body: JSON.stringify({
            sessionId: sessionId.value,
            agentAccount: agentAccount.value,
            speakerType: 1, // 1 = 客户
            content: contentToSend, // 发送转换后的文字
            customerName: customerName.value
          })
        })
        break // 成功则跳出重试循环
      } catch (err) {
        lastError = err
        console.warn(`发送消息第${attempt + 1}次失败:`, err)
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }
    }

    if (response && response.code === 0 && response.data && response.data.messageId) {
      // 用真实ID替换临时ID，防止轮询时重复
      const msgIndex = messages.value.findIndex(m => m.id === tempId)
      if (msgIndex !== -1) {
        messages.value[msgIndex].id = response.data.messageId
      }
      lastMessageId.value = Math.max(lastMessageId.value, response.data.messageId)
      // 发送成功后短暂延迟检查新消息
      setTimeout(() => checkNewMessages(), 500)
    } else if (response) {
      console.error('发送失败:', response.msg)
    } else {
      console.error('发送消息失败（重试耗尽）:', lastError)
    }
  } catch (error) {
    console.error('发送消息失败:', error)
  }
}

// 过滤后的订单列表
const filteredOrders = computed(() => {
  if (!orderSearchKeyword.value) return orders.value
  const keyword = orderSearchKeyword.value.toLowerCase()
  return orders.value.filter(order => 
    order.title.toLowerCase().includes(keyword) || 
    order.buyerName.toLowerCase().includes(keyword)
  )
})

// 处理快捷输入
const handleQuickInput = (text) => {
  // 需要弹出订单选择窗口的选项
  const orderPopupTriggers = [
    '选择订单',
    '违规如何申诉？',
    '对订单有疑问？',
    '咨询交易问题？',
    '咨询交易问题',
    '被处罚了如何申诉',
    '发货收货问题',
    '退款纠纷咨询'
  ]
  if (orderPopupTriggers.includes(text)) {
    // 重新加载订单数据
    loadOrders()
    showOrderPopup.value = true
    return
  }
  inputMessage.value = text
  messageInput.value?.focus()
}

// 发送订单消息
const sendOrderMessage = async (order) => {
  showOrderPopup.value = false

  // 构建订单卡片消息（使用特殊标记存储订单信息）
  const orderContent = `[订单:${order.title}:${order.price}:${order.image}]`

  const tempId = Date.now()
  const tempMessage = {
    id: tempId,
    agentAccount: agentAccount.value,
    speakerType: 1,
    content: orderContent,
    customerName: customerName.value,
    createdAt: new Date().toISOString()
  }
  messages.value.push(tempMessage)
  scrollToBottom()
  
  try {
    const response = await apiFetch('/api/chat/send', {
      method: 'POST',
      body: JSON.stringify({
        sessionId: sessionId.value,
        agentAccount: agentAccount.value,
        speakerType: 1,
        content: orderContent,
        customerName: customerName.value
      })
    })
    
    if (response.code === 0 && response.data?.messageId) {
      const msgIndex = messages.value.findIndex(m => m.id === tempId)
      if (msgIndex !== -1) {
        messages.value[msgIndex].id = response.data.messageId
      }
      lastMessageId.value = Math.max(lastMessageId.value, response.data.messageId)
    }
  } catch (error) {
    console.error('发送订单消息失败:', error)
  }
}

// 没有对应订单
const handleNoOrder = () => {
  showOrderPopup.value = false
  inputMessage.value = '我没有找到对应的订单'
  messageInput.value?.focus()
}

// 格式化消息内容（将文字表情转回emoji显示）
const formatContent = (content) => {
  return textToEmoji(content)
}

// 触发图片上传
const triggerImageUpload = () => {
  if (isUploading.value) return
  imageInput.value?.click()
}

// 处理图片上传
const handleImageUpload = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  // 验证文件类型
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/tiff']
  if (!allowedTypes.includes(file.type) && !file.name.match(/\.(heic|heif|tiff|tif)$/i)) {
    alert('只支持 JPG、PNG、GIF、WebP、HEIC、HEIF、TIFF 格式的图片')
    return
  }

  // 验证文件大小 (5MB)
  if (file.size > 5 * 1024 * 1024) {
    alert('图片大小不能超过 5MB')
    return
  }

  isUploading.value = true

  try {
    // 压缩图片（GIF除外）
    let fileToUpload = file
    if (file.type !== 'image/gif') {
      try {
        fileToUpload = await compressImageFast(file)
        console.log(`图片压缩: ${(file.size / 1024).toFixed(0)}KB -> ${(fileToUpload.size / 1024).toFixed(0)}KB`)
      } catch (error) {
        console.warn('图片压缩失败，使用原图:', error)
        fileToUpload = file
      }
    }

    const formData = new FormData()
    formData.append('file', fileToUpload)

    const response = await fetch(`${API_BASE_URL}/api/chat/upload-image`, {
      method: 'POST',
      body: formData
    })

    const result = await response.json()

    if (result.code === 0 && result.data?.imagePath) {
      // 发送图片消息
      const imageContent = `[图片:${result.data.imagePath}]`
      await sendImageMessage(imageContent)
    } else {
      alert(result.msg || '图片上传失败')
    }
  } catch (error) {
    console.error('图片上传失败:', error)
    alert('图片上传失败，请重试')
  } finally {
    isUploading.value = false
    // 清空文件输入
    if (imageInput.value) {
      imageInput.value.value = ''
    }
  }
}

// 发送图片消息
const sendImageMessage = async (imageContent) => {
  const tempId = Date.now()
  
  const tempMessage = {
    id: tempId,
    agentAccount: agentAccount.value,
    speakerType: 1,
    content: imageContent,
    customerName: customerName.value,
    createdAt: new Date().toISOString()
  }
  messages.value.push(tempMessage)
  scrollToBottom()
  
  try {
    const response = await apiFetch('/api/chat/send', {
      method: 'POST',
      body: JSON.stringify({
        sessionId: sessionId.value,
        agentAccount: agentAccount.value,
        speakerType: 1,
        content: imageContent,
        customerName: customerName.value
      })
    })
    
    if (response.code === 0 && response.data?.messageId) {
      const msgIndex = messages.value.findIndex(m => m.id === tempId)
      if (msgIndex !== -1) {
        messages.value[msgIndex].id = response.data.messageId
      }
      lastMessageId.value = Math.max(lastMessageId.value, response.data.messageId)
    }
  } catch (error) {
    console.error('发送图片消息失败:', error)
  }
}

// 判断是否是图片消息
const isImageMessage = (content) => {
  return content && content.startsWith('[图片:') && content.endsWith(']')
}

// 判断是否是订单卡片消息
const isOrderMessage = (content) => {
  return content && content.startsWith('[订单:') && content.endsWith(']')
}

// 解析订单卡片消息
const parseOrderMessage = (content) => {
  if (!isOrderMessage(content)) return null
  const inner = content.slice(4, -1) // 去掉 [订单: 和 ]
  // 标题可能包含冒号，图片URL也包含冒号，需要特殊处理
  // 格式: 标题:价格:图片URL
  // 从后往前找，最后部分是图片URL(http开头)，倒数第二是价格
  const lastHttpIndex = inner.lastIndexOf(':http')
  if (lastHttpIndex === -1) {
    // 没有图片URL的情况
    const lastColon = inner.lastIndexOf(':')
    return {
      title: inner.substring(0, lastColon),
      price: inner.substring(lastColon + 1),
      image: 'https://img.alicdn.com/imgextra/i4/O1CN01Z5paLz1O0zuCC7osS_!!6000000001644-0-tps-250-250.jpg'
    }
  }
  const imageUrl = inner.substring(lastHttpIndex + 1)
  const beforeImage = inner.substring(0, lastHttpIndex)
  const lastColon = beforeImage.lastIndexOf(':')
  return {
    title: beforeImage.substring(0, lastColon),
    price: beforeImage.substring(lastColon + 1),
    image: imageUrl
  }
}

// 判断是否是链接卡片消息
const isLinkCardMessage = (content) => {
  return content && content.startsWith('[卡片:') && content.endsWith(']')
}

// 解析链接卡片消息
const parseLinkCardMessage = (content) => {
  if (!isLinkCardMessage(content)) return { title: '', btnText: '查看详情', link: '' }
  var inner = content.slice(4, -1)
  var lastHttpIndex = inner.lastIndexOf(':http')
  if (lastHttpIndex === -1) {
    var parts = inner.split(':')
    if (parts.length >= 3) {
      var link = parts.pop()
      var btnText = parts.pop()
      return { title: parts.join(':'), btnText: btnText, link: link }
    }
    return { title: inner, btnText: '查看详情', link: '' }
  }
  var link = inner.substring(lastHttpIndex + 1)
  var beforeLink = inner.substring(0, lastHttpIndex)
  var lastColon = beforeLink.lastIndexOf(':')
  if (lastColon === -1) {
    return { title: beforeLink, btnText: '查看详情', link: link }
  }
  return {
    title: beforeLink.substring(0, lastColon),
    btnText: beforeLink.substring(lastColon + 1) || '查看详情',
    link: link
  }
}

// 打开链接卡片链接
const openCardLink = (link) => { if (link) window.open(link, '_blank') }

// 判断是否是支付卡片消息
const isPaymentCard = (content) => {
  if (!content) return false
  const match = content.match(/#payid(\d+)#/)
  return !!match
}

// 获取支付卡片订单号
const getPaymentOrderId = (content) => {
  if (!content) return ''
  const match = content.match(/#payid(\d+)#/)
  return match ? match[1] : ''
}

// 打开支付页面
const openPaymentPage = (content) => {
  const orderId = getPaymentOrderId(content)
  if (orderId) {
    window.open(`${window.location.origin}/payment?order=${orderId}`, '_blank')
  }
}

// 获取图片URL
const getImageUrl = (content) => {
  if (!isImageMessage(content)) return ''
  const imagePath = content.slice(4, -1) // 去掉 [图片: 和 ]

  // 如果是完整URL（OSS存储），直接返回
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath
  }

  // 否则是本地存储，拼接API路径
  return `${API_BASE_URL}/api/chat/image/${imagePath}`
}

// 预览图片
const previewImage = (content) => {
  const url = getImageUrl(content)
  if (url) {
    previewImageUrl.value = url
    imagePreviewVisible.value = true
  }
}

const closeImagePreview = () => {
  imagePreviewVisible.value = false
  previewImageUrl.value = ''
}

// 自适应消息轮询（WS连接时15s，断开时3s）
const scheduleMessagePoll = () => {
  const delay = chatWebSocket.isConnected() ? 15000 : 3000
  pollingInterval = setTimeout(async () => {
    await checkNewMessages()
    scheduleMessagePoll()
  }, delay)
}

// 开始轮询
const startPolling = () => {
  scheduleMessagePoll()
  // 每30秒上报一次已读位置
  readPositionInterval = setInterval(() => {
    if (lastMessageId.value && document.visibilityState === 'visible') updateReadPosition(lastMessageId.value)
  }, 30000)
  console.log('消息轮询已启动（自适应间隔）')
}

// 开始状态轮询（每2秒发送心跳，纯内存操作无服务器压力）
const startStatusPolling = () => {
  statusPollingInterval = setInterval(sendHeartbeat, 2000)
  console.log('客户心跳轮询已启动（每2秒）')
}

// 停止轮询
const stopPolling = () => {
  if (pollingInterval) {
    clearTimeout(pollingInterval)
    pollingInterval = null
  }
  if (statusPollingInterval) {
    clearInterval(statusPollingInterval)
    statusPollingInterval = null
  }
  if (readPositionInterval) {
    clearInterval(readPositionInterval)
    readPositionInterval = null
  }
  console.log('所有轮询已停止')
}

// 连接 WebSocket
const connectWebSocket = async () => {
  try {
    await chatWebSocket.connect(sessionId.value)
    wsUnsubscribe = chatWebSocket.onMessage((data) => {
      handleWebSocketMessage(data)
    })
    console.log('WebSocket 连接成功')
  } catch (e) {
    console.log('WebSocket 连接失败，使用轮询:', e)
  }
}

// 处理 WebSocket 消息
const handleWebSocketMessage = (data) => {
  if (data.type === 'newMessage' && data.sessionKey === sessionId.value) {
    // 如果是自己发送的消息（客户消息），跳过
    // 因为已经通过乐观更新添加了
    if (data.speakerType === 1 && data.customerName === customerName.value) {
      // 用服务端真实ID替换本地临时ID（tempId > 1000000000000）
      const tempIdx = messages.value.findIndex(m =>
        m.content === data.content && m.id > 1000000000000
      )
      if (tempIdx !== -1) {
        messages.value[tempIdx].id = data.id
        lastMessageId.value = Math.max(lastMessageId.value, data.id)
      }
      return
    }

    // 后端发送的是扁平格式，直接从 data 读取
    const exists = messages.value.some(m => m.id === data.id)
    if (!exists) {
      const newMessage = {
        id: data.id,
        speakerType: data.speakerType,
        content: data.content,
        customerName: data.customerName,
        createdAt: data.createdAt
      }
      messages.value.push(newMessage)
      lastMessageId.value = Math.max(lastMessageId.value, data.id)
      nextTick(() => scrollToBottom())
      // 如果是客服消息，播放提示音
      if (data.speakerType === 2) {
        playNotificationSound()
      }
      // 收到新消息就上报已读位置
      updateReadPosition(lastMessageId.value)
    }
  }
}

// 断开 WebSocket
const disconnectWebSocket = () => {
  if (wsUnsubscribe) {
    wsUnsubscribe()
    wsUnsubscribe = null
  }
}

// 处理页面可见性变化
const handleVisibilityChange = () => {
  if (!document.hidden) {
    // 页面变为可见：检查WS状态，重连如断开，加载完整消息，恢复轮询
    if (!chatWebSocket.isConnected()) {
      console.log('页面恢复可见，WebSocket断开，重新连接')
      connectWebSocket()
    }
    loadInitialMessages()
    updateCustomerOnlineStatus()
    startPolling()
    startStatusPolling()
  } else {
    // 页面隐藏：暂停轮询（停止定时器）
    console.log('页面隐藏，暂停轮询')
    stopPolling()
  }
}

// 验证客服账号是否存在（网络异常自动重试，直到成功）
const verifyAgent = async () => {
  isVerifying.value = true
  agentNotFound.value = false

  let verified = false
  while (!verified) { // eslint-disable-line no-constant-condition
    try {
      const response = await apiFetch(`/api/chat/verify-agent?agentAccount=${encodeURIComponent(agentAccount.value)}`)

      if (response.code === 0 && response.data?.exists) {
        agentNotFound.value = false
        if (response.data?.agentAccount) agentAccount.value = response.data.agentAccount
        verified = true
        return true
      } else {
        // 后端明确返回客服不存在
        agentNotFound.value = true
        verified = true
        return false
      }
    } catch (error) {
      console.error('验证客服账号失败，2秒后重试...', error)
      await new Promise(resolve => setTimeout(resolve, 2000))
    }
  }
}

// 从后端获取订单列表
const loadOrders = async () => {
  isLoadingOrders.value = true
  try {
    const orderId = urlOrderId.value
    
    // 如果URL中没有orderId参数，不显示任何订单
    if (!orderId) {
      orders.value = []
      isLoadingOrders.value = false
      return
    }
    
    // 根据订单ID获取特定订单
    const response = await fetch(`${API_BASE_URL}/api/order/${orderId}`)
    const result = await response.json()
    
    if (result.code === 0 && result.data) {
      const order = result.data
      orders.value = [{
        id: order.id,
        buyerName: order.buyerName || '买家',
        title: order.title,
        price: order.price,
        image: order.imagePath ? `${API_BASE_URL}/api/order/image/${order.imagePath}` : 'https://img.alicdn.com/imgextra/i4/O1CN01Z5paLz1O0zuCC7osS_!!6000000001644-0-tps-250-250.jpg'
      }]
    } else {
      orders.value = []
    }
  } catch (error) {
    console.error('加载订单失败:', error)
    orders.value = []
  } finally {
    isLoadingOrders.value = false
  }
}

// 生命周期钩子
onMounted(async () => {
  // 按接口计数访问 + 注册访客（sessionStorage去重，同一会话只计一次）
  const _urlParams = new URLSearchParams(window.location.search)
  const _arid = _urlParams.get('arid'), _artype = _urlParams.get('artype')
  if (_arid && _artype) {
    const _dedupKey = `_tv_${_artype}_${_arid}`
    if (!sessionStorage.getItem(_dedupKey)) {
      sessionStorage.setItem(_dedupKey, '1')
      fetch(`${API_BASE_URL}/api/chat/track-visit?linkId=${_arid}&linkType=${_artype}`, { method: 'POST' }).catch(() => {})
    }
  }
  fetch(`${API_BASE_URL}/api/chat/register-visit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionKey: sessionId.value, agent: agentAccount.value, customerName: customerName.value, platform: 'xy' }) }).catch(() => {})

  console.log('客户聊天系统初始化:', {
    sessionId: sessionId.value,
    customerName: customerName.value,
    agentAccount: agentAccount.value
  })

  // 验证客服账号
  const agentExists = await verifyAgent()
  if (!agentExists) {
    console.log('客服账号不存在，停止初始化')
    isVerifying.value = false
    return
  }

  // 加载数据
  await loadOrders()
  await loadInitialMessages()

  // 完成验证
  isVerifying.value = false

  // 启动轮询和WebSocket
  connectWebSocket()
  startPolling()
  startStatusPolling()
  updateCustomerOnlineStatus()

  document.addEventListener('visibilitychange', handleVisibilityChange)
  // 多种方式监听页面关闭，确保移动端也能正确触发
  window.addEventListener('beforeunload', setCustomerOffline)
  window.addEventListener('pagehide', setCustomerOffline)  // 移动端更可靠
  window.addEventListener('unload', setCustomerOffline)    // 备用
})

onBeforeUnmount(() => {
  stopPolling()
  disconnectWebSocket()
  setCustomerOffline()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('beforeunload', setCustomerOffline)
  window.removeEventListener('pagehide', setCustomerOffline)
  window.removeEventListener('unload', setCustomerOffline)
  console.log('客户聊天系统已销毁')
})
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-tap-highlight-color: transparent;
}

html, body {
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: fixed;
  touch-action: manipulation;
  -webkit-overflow-scrolling: touch;
  /* 禁止双击缩放 */
  touch-action: pan-x pan-y;
}

#app {
  height: 100vh;
  height: var(--app-height, 100dvh); /* 动态视口高度，处理移动端地址栏和底栏 */
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
}

.chat-container {
  background: #f7f7f7;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: #f7f7f7;
}

.welcome-message {
  display: flex;
  flex-direction: column;
  color: #8e8e8e;
  font-size: 13px;
  align-items: center;
  margin-bottom: 18px;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
}

/* 消息样式 */
.message {
  display: flex;
  margin-bottom: 16px;
}

.message.agent {
  align-items: flex-start;
}

.message.customer {
  flex-direction: row-reverse;
  align-items: flex-end;
}

.avatar {
  min-height: 36px;
  min-width: 36px;
  max-width: 36px;
  max-height: 36px;
  border-radius: 0;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar img {
  height: 100%;
  width: 100%;
}

.message-content {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.message.agent .message-content {
  margin-left: 3px;
  align-items: flex-start;
}

.message.customer .message-content {
  margin-right: 3px;
  align-items: flex-end;
}

.sender-name {
  color: #8e8e8e;
  font-size: 12px;
  margin-left: 1px;
  margin-bottom: 4px;
}

.message-bubble {
  padding: 10px;
  background: white;
  border-radius: 12px;
  width: fit-content;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  letter-spacing: 0.5px;
  user-select: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji', sans-serif;
  -webkit-user-select: auto;
  font-size: 15px;
  max-width: 70%;
}

.message-bubble.customer {
  background: #fbe440;
}

/* 支付卡片样式 */
.payment-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 10px 12px;
  border-radius: 12px;
  gap: 12px;
  width: 100%;
  max-width: 240px;
}

.payment-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.payment-card-header img {
  height: 20px;
}

.payment-card-order {
  font-size: 13px;
  color: #666;
}

.payment-card-btn {
  background: #fbe440;
  color: #000;
  text-align: center;
  padding: 8px 0;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

/* 订单卡片消息样式 */
.order-card-msg {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  max-width: 260px;
}
.order-card-inner {
  display: flex;
  font-size: 0.875rem;
  padding: 0.75rem;
  min-height: 96px;
}
.order-card-img {
  width: 80px;
  height: 80px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  margin-right: 10px;
}
.order-card-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.order-card-detail {
  display: flex;
  flex: 1;
}
.order-card-name {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 400;
  color: #111;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}
.order-card-price-wrap {
  flex-shrink: 0;
  text-align: right;
  margin-left: 8px;
}
.order-card-currency {
  font-size: 0.75rem;
  color: #111;
}
.order-card-price {
  font-size: 1rem;
  font-weight: 600;
  color: #111;
}
.order-card-count {
  display: block;
  font-size: 0.75rem;
  color: #999;
  margin-top: 2px;
}
.order-card-status {
  font-size: 0.75rem;
  color: #ff5000;
  margin-top: auto;
}

/* 链接卡片消息样式 */
.link-card-msg {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  max-width: 260px;
}
.link-card-inner {
  padding: 0.75rem;
}
.link-card-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111;
  margin: 0 0 10px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.link-card-btn {
  display: block;
  width: 100%;
  padding: 8px 0;
  text-align: center;
  background: #007aff;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
}
.link-card-btn:active {
  opacity: 0.8;
}

.payment-card-btn:active {
  opacity: 0.8;
}

.special-message {
  background: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  padding-bottom: 0;
  font-size: 15px;
}

.order-button {
  background: #fbe440;
  text-align: center;
  border-radius: 24px;
  padding: 7px 0;
  font-size: 14px;
  margin-top: 12px;
  font-weight: bold;
  margin-bottom: 12px;
  cursor: pointer;
}

.quick-question {
  padding: 12px 0;
  font-size: 13.5px;
  border-top: .5px solid #dbdbdb;
  position: relative;
  cursor: pointer;
}

.quick-question::after {
  content: "";
  display: block;
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.highlight-text {
  text-decoration: underline;
  text-decoration-color: #fbe440;
  text-decoration-thickness: 6px;
  text-underline-offset: -1.5px;
  font-weight: bold;
}

.input-area {
  background: #f5f5f5;
  padding: 8px;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.quick-replies {
  width: 100%;
  padding: 10px 8px;
  font-size: .85rem;
}

.quick-replies-container {
  display: flex;
  gap: 8px;
  word-break: keep-all;
  overflow-x: auto;
}

.quick-reply {
  padding: 7px 16px;
  border-radius: 16px;
  background-color: #fff;
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid #e0e0e0;
}

.quick-reply.active {
  border: 1px solid #ffe60d;
}

.input-row {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  box-sizing: border-box;
  gap: 8px;
}

.message-input {
  font-size: 16px; /* 至少16px，防止iOS自动缩放 */
  border: none;
  border-radius: 8px;
  font-family: inherit;
  padding: 8px 12px;
  width: 100%;
  box-sizing: border-box;
  background: white;
  flex: 1;
  /* 防止iOS缩放 */
  -webkit-text-size-adjust: 100%;
  -webkit-appearance: none;
  appearance: none;
}

.message-input:focus {
  outline: none;
  /* 防止聚焦时缩放 */
  font-size: 16px;
}

.upload-button {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
  flex-shrink: 0;
}

.upload-icon {
  width: 32px;
  height: 32px;
}

.send-button {
  word-break: keep-all;
  padding: 8px 16px;
  background: #ffe60d;
  border: none;
  border-radius: 18px;
  font-size: 14px;
  color: #2d2d2d;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.send-button:disabled {
  background: #cccccc;
  color: #666666;
  cursor: not-allowed;
}

/* 图片上传相关样式 */
.upload-button.uploading {
  opacity: 0.6;
  cursor: not-allowed;
}

.upload-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-top-color: #666;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 图片消息样式 */
.message-image {
  max-width: 200px;
  max-height: 200px;
  min-width: 100px;
  min-height: 100px;
  background-color: #f0f0f0;
  border-radius: 12px;
  cursor: pointer;
  object-fit: cover;
  border: 1px solid #e0e0e0;
}

.message-image.customer-image {
  border-color: #fbe440;
}

/* 错误页面样式 */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  height: 100dvh;
  background: #f5f5f5;
  padding: 20px;
  text-align: center;
}

.error-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.error-title {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
}

.error-message {
  font-size: 16px;
  color: #666;
  margin-bottom: 8px;
}

.error-hint {
  font-size: 14px;
  color: #999;
  max-width: 280px;
  line-height: 1.5;
}

/* 加载页面样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  height: 100dvh;
  background: #f5f5f5;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #fbe440;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

.loading-text {
  font-size: 14px;
  color: #666;
}

/* 订单选择弹窗样式 */
.order-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.order-popup {
  background: #fff;
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.popup-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.popup-close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popup-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

/* Tab切换 */
.order-tabs {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  padding: 0 16px;
  gap: 0;
}

.tab-btn {
  padding: 12px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
}

.tab-btn.active {
  color: #111;
  font-weight: 600;
  border-bottom-color: #111;
}

/* 搜索框 */
.order-search {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.search-icon {
  color: #999;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 4px 0;
  font-size: 14px;
  outline: none;
  color: #333;
}
.search-input::placeholder {
  color: #ccc;
}

/* 订单列表 */
.order-list {
  max-height: 50vh;
  overflow-y: auto;
  padding: 0 6px;
  background: #f5f5f5;
}

.order-card {
  background: #fff;
  border-radius: 12px;
  margin: 8px 0;
  overflow: hidden;
}

.order-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 0;
}

.order-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.order-logo {
  width: 20px;
  height: 18px;
  flex-shrink: 0;
}

.order-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.order-name {
  font-size: 14px;
  color: #111;
  font-weight: 500;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-status {
  font-size: 12px;
  color: #ff5000;
  flex-shrink: 0;
  margin-left: 8px;
}

.order-body {
  display: flex;
  padding: 10px 12px 12px;
  gap: 10px;
}

.order-img {
  width: 64px;
  height: 64px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}

.order-body-right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.order-detail {
  display: flex;
  gap: 8px;
}

.order-info {
  flex: 1;
  min-width: 0;
}

.order-title {
  font-size: 14px;
  color: #111;
  margin: 0;
  font-weight: 400;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.order-aside {
  flex-shrink: 0;
  text-align: right;
}

.order-price {
  color: #111;
  font-weight: 500;
  white-space: nowrap;
}

.price-currency {
  font-size: 12px;
}

.price-value {
  font-size: 15px;
  font-weight: 600;
}

.order-count {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.order-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.order-send-btn {
  background: #ff5000;
  border: none;
  padding: 6px 20px;
  border-radius: 16px;
  font-size: 13px;
  color: #fff;
  cursor: pointer;
  font-weight: 500;
}

/* 空状态 */
.order-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
}

.empty-img {
  width: 120px;
  height: 100px;
  margin-bottom: 12px;
}

.empty-tip {
  font-size: 14px;
  color: #999;
  margin: 0;
}

/* 弹窗底部 */
.popup-footer {
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
}

.no-order-btn {
  width: 100%;
  padding: 14px;
  background: #f5f5f5;
  border: none;
  border-radius: 24px;
  font-size: 16px;
  color: #333;
  cursor: pointer;
}

/* 淡入动画 */
.fade-enter-active {
  transition: opacity 0.5s ease-out;
}
.fade-enter-from {
  opacity: 0;
}
/* 图片预览模态框 */
.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.image-preview-modal.active {
  display: flex;
}
.image-preview-content {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
}

</style>
